import { collection, doc, setDoc, getDoc, query, orderBy, getDocs, deleteDoc, serverTimestamp, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import type { CompanyAnalysis, SavedMemo } from '../types/geminiTypes';
import * as Sentry from "@sentry/react";

export async function saveMemo(uid: string, ticker: string, analysisResult: CompanyAnalysis): Promise<void> {
  if (!uid || !ticker || !analysisResult) return;
  try {
    const memoRef = doc(db, 'users', uid, 'memos', ticker.toUpperCase());
    const saveData = {
      ticker: ticker.toUpperCase(),
      companyName: analysisResult.name || '',
      verdict: analysisResult.oracleFramework?.decision?.value || 'PASS',
      intrinsicValue: (analysisResult as any).scenarios?.base?.intrinsicValuePerShare || analysisResult.oracleFramework?.oracleIntrinsicValue || 0,
      runAt: serverTimestamp(),
      uid,
      analysisResult
    };
    await setDoc(memoRef, saveData); fetch("/api/telemetry", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ event: "memo_saved", properties: { ticker, verdict: saveData.verdict, uid } }) }).catch(e => console.error(e));
  } catch (error) {
    console.error(`[memoService] Failed to save memo for ${ticker}:`, error);
    Sentry.captureException(error);
  }
}

export async function getMemo(uid: string, ticker: string): Promise<SavedMemo | null> {
  if (!uid || !ticker) return null;
  try {
    const memoRef = doc(db, 'users', uid, 'memos', ticker.toUpperCase());
    const snap = await getDoc(memoRef);
    if (snap.exists()) {
      return snap.data() as SavedMemo;
    }
  } catch (error) {
    console.error(`[memoService] Failed to get memo for ${ticker}:`, error);
    Sentry.captureException(error);
  }
  return null;
}

export function subscribeToMemos(uid: string, onUpdate: (memos: SavedMemo[]) => void): () => void {
  if (!uid) return () => {};
  const memosRef = collection(db, 'users', uid, 'memos');
  const q = query(memosRef, orderBy('runAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const memos = snapshot.docs.map(doc => doc.data() as SavedMemo);
    onUpdate(memos);
  }, (error) => {
    console.error('[memoService] Error subscribing to memos:', error);
    Sentry.captureException(error);
  });
}

export async function deleteMemo(uid: string, ticker: string): Promise<void> {
  if (!uid || !ticker) return;
  try {
    const memoRef = doc(db, 'users', uid, 'memos', ticker.toUpperCase());
    await deleteDoc(memoRef);
  } catch (error) {
    console.error(`[memoService] Failed to delete memo for ${ticker}:`, error);
    Sentry.captureException(error);
  }
}
